import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, KeyRound, Phone } from 'lucide-react';
import { api, ApiError, setToken } from '../api';
import { useAuth } from '../auth';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Logo } from '../components/Logo';
import { ThemeToggle } from '../components/layout/ThemeToggle';

// Admin sign-in is phone + a one-time code. The code is delivered to the
// admin's Telegram / WhatsApp by the backend (POST /api/auth/admin/otp/*).
// In dev the request response carries `dev_code` so the flow is testable
// without a messaging channel wired.

interface OtpRequestResponse {
  sent: boolean;
  dev_code?: string;
}

interface VerifyResponse {
  token: string;
  user: { id: string; phone: string; role: string };
}

// Pull a human-readable message out of an ApiError body, falling back to a
// status-based default.
function errorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: unknown } | null;
    if (body && typeof body.error === 'string') return body.error;
    return `${fallback} (${e.status})`;
  }
  return fallback;
}

export function LoginPage() {
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('+65');
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function requestCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await api<OtpRequestResponse>('/api/auth/admin/otp/request', {
        method: 'POST',
        json: { phone },
      });
      // Dev convenience — prefill the code the backend echoed back.
      setDevCode(r.dev_code ?? null);
      setCode(r.dev_code ?? '');
      setStep('code');
    } catch (e) {
      setErr(errorMessage(e, 'Could not send a code.'));
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await api<VerifyResponse>('/api/auth/admin/otp/verify', {
        method: 'POST',
        json: { phone, code },
      });
      setToken(r.token);
      await refresh();
      nav('/');
    } catch (e) {
      setErr(errorMessage(e, 'Sign-in failed.'));
    } finally {
      setBusy(false);
    }
  }

  function backToPhone() {
    setStep('phone');
    setCode('');
    setDevCode(null);
    setErr(null);
  }

  return (
    <div className="relative flex min-h-dvh items-center justify-center bg-bg p-4">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>

      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <Logo width={180} />
          <p className="mt-3 text-xs font-medium uppercase tracking-[0.2em] text-text-muted">
            Admin
          </p>
          <p className="mt-3 text-sm text-text-muted">
            {step === 'phone'
              ? 'Sign in with your admin phone number.'
              : 'Enter the 6-digit code we sent you.'}
          </p>
        </div>

        <Card>
          <CardContent className="p-6">
            {step === 'phone' ? (
              <form onSubmit={requestCode} className="flex flex-col gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="phone">Phone</Label>
                  <div className="relative">
                    <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
                    <Input
                      id="phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.trim())}
                      placeholder="+6591234567"
                      inputMode="tel"
                      className="pl-9"
                      autoComplete="tel"
                      autoFocus
                    />
                  </div>
                  <p className="text-xs text-text-muted">
                    We&rsquo;ll send a one-time code to your admin Telegram or
                    WhatsApp.
                  </p>
                </div>

                {err && (
                  <div className="rounded-md border border-error/40 bg-error-tint px-3 py-2 text-sm text-error-on-tint">
                    {err}
                  </div>
                )}

                <Button type="submit" disabled={busy} size="lg" className="w-full">
                  <Phone className="h-4 w-4" />
                  {busy ? 'Sending…' : 'Send login code'}
                </Button>
              </form>
            ) : (
              <form onSubmit={verifyCode} className="flex flex-col gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="code">6-digit code</Label>
                  <div className="relative">
                    <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
                    <Input
                      id="code"
                      value={code}
                      onChange={(e) =>
                        setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                      }
                      placeholder="000000"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      className="pl-9 tracking-[0.3em]"
                      autoFocus
                    />
                  </div>
                  <p className="text-xs text-text-muted">
                    Sent to {phone}.
                  </p>
                  {devCode && (
                    <p className="text-xs font-medium text-brand-700 dark:text-brand-500">
                      Dev mode — code: {devCode}
                    </p>
                  )}
                </div>

                {err && (
                  <div className="rounded-md border border-error/40 bg-error-tint px-3 py-2 text-sm text-error-on-tint">
                    {err}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={busy || code.length !== 6}
                  size="lg"
                  className="w-full"
                >
                  <KeyRound className="h-4 w-4" />
                  {busy ? 'Signing in…' : 'Sign in'}
                </Button>

                <button
                  type="button"
                  onClick={backToPhone}
                  className="mx-auto flex items-center gap-1 text-xs font-medium text-text-muted hover:text-text"
                >
                  <ArrowLeft className="h-3 w-3" />
                  Use a different number
                </button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-xs text-text-muted">
          GoDrop · v0.1.0-mvp
        </p>
      </div>
    </div>
  );
}
